import {common, priority} from '@priority-software/priority-proto-api/lib';
import {observable, action, makeObservable} from 'mobx';
import {apiLocker} from './API_Locker';
import {Record} from './Record';
import {Column} from './Column';
import {OpenTableMethods} from '../../features/Form/components/Table/types';
import {getKeyByLang} from '../../features/Form/utils/custom-properties';
import {setFormCustomData} from '../../services/form/form';
import {Actions, SavedSearch, SubFormsList} from '../../features/Form/types';
import {ColumnsTabsDesignType, createEmptyRow, formViewColumnsTabsMock} from './utils';
import {Filter} from './Filter';
import {isFormReadOnly} from '../../features/Form/actions/utils/isFormReadOnly';
import {FormHelp} from '../../_shared/Help/types';
import {SidePanelSubforms} from '../../features/Form/consts/EntityTypes';
import {LexicalEditor} from 'lexical';
import {FormMetaStoreKeys} from './enums';
import {defaultHeight} from '../../features/Form/consts/grid';
import {ISort} from './Sort';
import {isDevEnvProcess} from '../../utils/platform';
import {HelpBook} from './HelpBook';
import {SearchStore} from './SearchStore';

export type FormActiveElement = HTMLInputElement | HTMLButtonElement | HTMLLinkElement | null;

export enum FormViewEnum {
    table = 'table',
    form = 'form'
}

export class Form {
    level: number;
    ename: string;
    form: priority.netitems.form.IStartRs;
    columnsMap: Map<number, Column> = new Map<number, Column>();
    sort: Array<ISort> = [];
    _visibleColumns: Array<Column> = [];
    keyColumns: Array<Column> = [];
    parentForm: Form | null = null;
    key: string = '';
    id: string = '';
    formActiveElement: FormActiveElement;
    statuses: priority.netitems.form.IStatus[] = [];
    preventScreenRender: boolean;
    searchModeData = {};
    fastSearchQuery: string;
    sidePanelSubforms = null;
    originHTMLContent: string = '';
    _htmlEditorInstance: LexicalEditor = null;
    customColumnsWidth: {[key: number]: number} = {};
    tableHeight: number;
    tableScrollOffset: {top: number; left: number} = {top: 0, left: 0};
    columnsTabs: ColumnsTabsDesignType = [];
    abortController: AbortController | undefined = undefined;
    isDevEnv = isDevEnvProcess();
    maxFetch: number;
    searchStore = new SearchStore(this);

    @observable filters: Array<Filter> = [];
    @observable formHelp: FormHelp | null = null;
    @observable view: FormViewEnum = FormViewEnum.table;
    @observable collapsed: boolean = false;
    @observable HTMLContent: string = '';
    @observable fixedColumnsNumber: number = 1;
    @observable currentColumnId: number;
    @observable currentRow: number;
    @observable records: Record[] = [];
    @observable searchModeLevel: number = 0;
    @observable tableAPI: OpenTableMethods;
    @observable subFormsList: SubFormsList;
    @observable activeSubForm: Form | null = null;
    @observable activeTabSubForm: Form | null = null;
    @observable actions: Actions = [];
    @observable shouldFetchMore: boolean = false;
    @observable paginationOffset: number = 0;
    @observable hasMore: boolean = false;
    @observable allRecordsCount: number = 0;
    @observable hiddenRecords: Array<Array<priority.netitems.form.IField>> = [];
    @observable helpBook = new HelpBook();

    constructor(
        ename: string,
        form: priority.netitems.form.IStartRs,
        columns: priority.netitems.form.IColumnsRs,
        level: number,
        formCustomData?: any
    ) {
        makeObservable(this);
        this.ename = ename;
        this.form = form;
        this.level = level;
        // Handle form meta data before processing the columns
        this.handleFormMetaData(formCustomData);
        this.setColumns(columns);
        this.currentColumnId = this.columns?.[0]?.id;
        this.currentRow = 0;
        this._visibleColumns = [...this.columnsMap.values()].filter(c => !c.hidden);
        this.keyColumns = [...this.columnsMap.values()].filter(c => c.Key);
        this.key = Date.now().toString();
        this.setRecords([]); // set default rows
        this.columnsTabs = formViewColumnsTabsMock(this._visibleColumns);
        this.id = `${ename}-level-${level}`;
    }

    get session(): string {
        return this.form?.Session;
    }

    get title(): string {
        return this.form?.Title;
    }

    get columns(): Column[] {
        return [...this.columnsMap.values()];
    }

    get visibleColumns(): Column[] {
        return this._visibleColumns;
    }

    get currentColumn(): Column | null {
        return this.columnsMap.get(this.currentColumnId) || null;
    }

    get isReadonly(): boolean {
        return isFormReadOnly(this.form);
    }

    get isNoDelete(): boolean {
        return this.form?.Privileges === 1;
    }

    get currentRecord(): Record {
        return this.records[this.currentRow];
    }

    activeField = () => {
        return this.records[this.currentRow]?.fieldsMap.get(this.currentColumnId);
    };

    getColumnByID(id: number): Column {
        return this.columns.find(c => c.ID === id);
    }

    getColumnByName(name: string): Column {
        return this.columns.find(c => c.Name === name);
    }

    setFormActiveElement = (el: FormActiveElement) => {
        this.formActiveElement = el;
    };

    setParentForm = (parentForm: Form) => {
        this.parentForm = parentForm;

        parentForm.setSubForm(this);
    };

    setColumns = (columns: priority.netitems.form.IColumnsRs) => {
        let visibleIndex = 0;

        columns?.Columns.forEach(column => {
            const colInstance = new Column(this, column);
            if (!colInstance.hidden) {
                colInstance.setColumnIndex(visibleIndex);
                visibleIndex++;
            }
            this.columnsMap.set(Number(column.ID), colInstance);
        });
    };

    handleFormMetaData = customData => {
        const fixedColumnKey = getKeyByLang(
            this.ename,
            'CUSTOM_DATA',
            FormMetaStoreKeys.fixedColumns
        );
        const columnsWidthKey = getKeyByLang(
            this.ename,
            'CUSTOM_DATA',
            FormMetaStoreKeys.customColumnsWidth
        );
        const tableHeightKey = getKeyByLang(
            this.ename,
            'CUSTOM_DATA',
            `${FormMetaStoreKeys.tableHeight}:${this.level}`
        );

        if (customData) {
            if (customData[fixedColumnKey]) {
                this.fixedColumnsNumber = customData[fixedColumnKey].Int;
            }
            if (customData[columnsWidthKey]) {
                this.customColumnsWidth = JSON.parse(customData[columnsWidthKey].Str);
            }
            if (customData[tableHeightKey]) {
                this.tableHeight = customData[tableHeightKey].Int || defaultHeight;
            }
        }
    };

    updateSearchModeCellValue = (val: string, colId: number, rowIndex: number) => {
        this.searchModeData = {
            ...this.searchModeData,
            [rowIndex]: {
                ...(this.searchModeData[rowIndex] || {}),
                [colId]: val
            }
        };
    };

    clearSearchModeData = () => (this.searchModeData = {});

    setAbortController = () => {
        this.abortController = new AbortController();
    };
    onCancelRs = () => {
        this.abortController.abort();
        this.abortController = undefined;
    };

    setHiddenRecordKeys = (recordKeys: Array<priority.netitems.form.IField>) => {
        this.hiddenRecords = [...this.hiddenRecords, recordKeys];
    };

    resetHiddenRecordKeys = () => {
        this.hiddenRecords = [];
    };

    @action toggleFormView = (view: FormViewEnum) => {
        this.view = view;
    };

    @action toggleViewState = (collapsed: boolean) => {
        this.collapsed = collapsed;
    };

    @action setSubForm = (form: Form) => {
        this.activeSubForm = form;
        if (!form?.isPanelTypeSubform()) {
            this.activeTabSubForm = form;
        }
    };

    isPanelTypeSubform = () => {
        const subform = this.parentForm?.subFormsList?.find(s => s.Ename === this.ename);
        return SidePanelSubforms.includes(subform?.Type);
    };

    @action setFormActions = (actions: Actions = []) => (this.actions = actions);

    @action updateNewSearch = (search: SavedSearch) => {
        // Todo: this old logic should be rewritten
        const {savedSearches, setFormSavedSearches} = this.searchStore;
        const newSearchIndex = savedSearches.findIndex(srch => !srch.isSaved);

        const updatedSearches =
            newSearchIndex > -1
                ? savedSearches.map((srch, index) => (index === newSearchIndex ? search : srch))
                : [search, ...savedSearches];

        setFormSavedSearches(updatedSearches);
    };

    @action
    setTableAPI = (api: OpenTableMethods) => (this.tableAPI = api);

    @action setSubForms = (data: Array<priority.netitems.form.ISubform> = []) => {
        this.subFormsList = data;
        this.setSidePanelSubform();
    };

    setSidePanelSubform = () => {
        this.subFormsList.forEach(s => {
            if (s.Type && SidePanelSubforms.includes(s.Type)) {
                this.sidePanelSubforms = this.sidePanelSubforms ?? {};
                this.sidePanelSubforms[s.Type] = s.Ename;
            }
        });
    };

    @action setCurrentColumnId = (value: number) => {
        if (this.currentColumnId !== value) {
            this.currentColumn.setEditMode(false);
            this.currentColumnId = value;
        }
    };

    @action setCurrentColumnIdByIndex = (index: number) => {
        const ID = this._visibleColumns[index]?.id;
        this.setCurrentColumnId(ID);
    };

    @action setCurrentRow = (rowIndex: number) => {
        this.currentRow = rowIndex;
    };

    @action updateFixedColumnsCount = async (value: number) => {
        this.fixedColumnsNumber = value;
        const data = {[FormMetaStoreKeys.fixedColumns]: new common.Constant({Int: value})};
        await setFormCustomData(data, this.ename, false, true);
    };

    @action setHTMLContent = (content: string = '') => {
        this.HTMLContent = content;
    };

    setOriginHTMLContent = (content: string = '') => {
        this.originHTMLContent = content;

        if (this.HTMLContent !== content) {
            this.setHTMLContent(content);
        }
    };

    setEditorInstance = editorInstance => {
        this._htmlEditorInstance = editorInstance;
    };

    addFilter = (filter: Filter) => {
        this.filters.push(filter);
    };

    updateFilters = (filter: Filter[] = []) => {
        this.filters = filter;
    };

    resetFilters = () => {
        this.filters = [];
        this.searchStore.setCurrentSearchId(null);
    };

    setSort = (sort: ISort) => {
        this.sort.push(sort);
    };

    updateSort = (sort: ISort[]) => {
        this.sort = sort;
    };
    resetSort = () => {
        this.sort = [];
    };

    createEmptyRows = (count: number = 1): priority.netitems.form.IRecord[] => {
        return new Array(count).fill(null).map(() => createEmptyRow(this.columns));
    };

    setStatuses = statuses => {
        this.statuses = statuses;
    };

    @action
    updateRecordValues = (
        recordIndex: number,
        fields: priority.netitems.form.IField[] | null,
        isNew?: boolean
    ) => {
        this.records = this.records.map(record => {
            if (record.recordIndex === recordIndex) {
                record.setFields(fields);
                if (isNew !== undefined) {
                    record.isNew = isNew;
                }
            }
            return record;
        });
    };

    @action
    setRecords = (
        records: priority.netitems.form.IRecord[] = [],
        paginationOffset?: number,
        hasMoreData?: boolean
    ) => {
        this.isDevEnv && console.time(`setRecords - ${this.ename}`);
        this.records = records.map((record, index) => new Record(this, record, index));
        const lastRecord = this.records[this.records.length - 1];
        // The last row should always be an empty row
        if (this.records.length === 0) {
            new Array(2).fill(0).forEach((r, index) => {
                this.insertEmptyRecord(index);
            });
        } else if (!lastRecord?.isNew) {
            this.insertEmptyRecord(this.records.length);
        }
        this.isDevEnv && console.timeEnd(`setRecords - ${this.ename}`);

        this.setPaginationOffset(paginationOffset, hasMoreData);
    };

    @action
    mergeRecords = (records: Record[], newIRecords: priority.netitems.form.IRecord[] = []) => {
        this.isDevEnv && console.time('setRecords');
        const newRecords = newIRecords.map(
            (record, index) => new Record(this, record, records.length + index)
        );
        this.records = [...records, ...newRecords];
        this.isDevEnv && console.timeEnd('setRecords');
    };

    @action
    resetRecords = () => {
        this.setRecords([]);
        this.activeSubForm?.resetRecords();
    };

    @action
    setSearchModeLevel = (searchModeLevel: number) => {
        if (this.searchModeLevel !== searchModeLevel) {
            this.searchModeLevel = searchModeLevel;
            this.setCurrentRow(searchModeLevel === 0 ? 0 : 1);
        }
    };

    @action
    insertEmptyRecord = (position: number, isTemporarily?: boolean) => {
        const emptyRow = this.createEmptyRows(1)[0];

        this.records = [
            ...this.records.slice(0, position + 1),
            new Record(this, emptyRow, 0, true, isTemporarily),
            ...this.records.slice(position + 1, this.records.length)
        ].map((record, i) => {
            record.setRecordIndex(i);
            return record;
        });
    };

    @action
    resetRecord = (rowInd: number) => {
        this.records[rowInd] = new Record(this, createEmptyRow(this.columns), rowInd, true);
    };

    setShouldFetchMore = (shouldFetchMore: boolean) => {
        this.shouldFetchMore = shouldFetchMore;
    };

    setFastSearchQuery = (searchQuery: string) => {
        this.fastSearchQuery = searchQuery;
    };

    getFastSearchQuery = () => {
        return this.fastSearchQuery;
    };

    isApiLock = () => {
        const currScreenLockerData = apiLocker.getDataBySession(this.session);
        return currScreenLockerData?.navigationBlocked || currScreenLockerData?.locked;
    };

    @action
    deleteClientRecord = (recordIndex: number) => {
        // delete record by index on client side
        this.records.splice(recordIndex, 1);
        // rearrange records indexes after deletion of a record
        this.records.map(record => {
            if (record.recordIndex > recordIndex) {
                record.setRecordIndex(record.recordIndex - 1);
            }
            return record;
        });
    };

    @action
    setPaginationOffset(paginationOffset: number = 0, hasMore: boolean = false) {
        this.paginationOffset = paginationOffset;
        this.hasMore = hasMore;
    }

    @action
    setHasMore(hasMore: boolean) {
        this.hasMore = hasMore;
    }

    @action
    setAllRecordsCount(count: number = 0) {
        this.allRecordsCount = count;
    }

    get isEmpty() {
        return !this.records.find(r => !r.isNew);
    }
}
